
import React from 'react';
import { Card } from "@/components/ui/card";
import { 
  AreaChart,
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';
import { useAdaptiveDifficulty } from '@/hooks/useAdaptiveDifficulty';
import { Lightbulb, ArrowUp, ArrowDown, Zap } from 'lucide-react';
import type { PerformanceData } from './types';

interface AdaptiveDifficultyProps {
  performanceData: PerformanceData[];
}

export const AdaptiveDifficulty: React.FC<AdaptiveDifficultyProps> = ({ 
  performanceData 
}) => {
  const { recommendedDifficulty } = useAdaptiveDifficulty('medium');

  // Process performance data for visualization
  const difficultyData = React.useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    
    // Sort by date
    const sortedData = [...performanceData].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    
    // Group entries by day
    const dailyData: Record<string, { 
      date: string;
      scoreEasy: number; 
      scoreMedium: number; 
      scoreHard: number;
      countEasy: number;
      countMedium: number;
      countHard: number;
      adaptiveScore: number;
    }> = {};
    
    sortedData.forEach(entry => {
      const date = new Date(entry.created_at).toISOString().split('T')[0];
      
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          scoreEasy: 0,
          scoreMedium: 0,
          scoreHard: 0,
          countEasy: 0,
          countMedium: 0,
          countHard: 0,
          adaptiveScore: 0
        };
      }
      
      // Add scores by difficulty
      if (entry.difficulty === 'easy') {
        dailyData[date].scoreEasy += entry.score;
        dailyData[date].countEasy++;
      } else if (entry.difficulty === 'medium') {
        dailyData[date].scoreMedium += entry.score;
        dailyData[date].countMedium++;
      } else if (entry.difficulty === 'hard') {
        dailyData[date].scoreHard += entry.score;
        dailyData[date].countHard++;
      }
      
      // Calculate a weighted adaptive score
      // Weight: easy = 1, medium = 2, hard = 3
      const difficultyWeight = 
        entry.difficulty === 'easy' ? 1 : 
        entry.difficulty === 'medium' ? 2 : 3;
        
      dailyData[date].adaptiveScore += entry.score * difficultyWeight;
    });
    
    // Convert to array and calculate daily averages
    return Object.values(dailyData).map(day => ({
      date: day.date,
      // Calculate averages or use 0 if no attempts
      easy: day.countEasy > 0 ? day.scoreEasy / day.countEasy : 0,
      medium: day.countMedium > 0 ? day.scoreMedium / day.countMedium : 0,
      hard: day.countHard > 0 ? day.scoreHard / day.countHard : 0,
      adaptive: day.adaptiveScore / 
        (day.countEasy + day.countMedium * 2 + day.countHard * 3 || 1)
    }));
  }, [performanceData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDifficultyLevel = (score: number) => {
    if (score > 85) return 'Mastering';
    if (score > 70) return 'Advancing';
    if (score > 50) return 'Developing';
    return 'Beginning';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2 text-purple-800">
          <Lightbulb className="w-6 h-6 text-purple-600" />
          Adaptive Difficulty Analysis
        </h2>
        <div className="px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full flex items-center gap-2">
          <span className="text-sm font-medium text-purple-800">
            Recommended Level: 
          </span>
          <span className={`text-sm font-bold ${
            recommendedDifficulty === 'hard' ? 'text-red-600' : 
            recommendedDifficulty === 'medium' ? 'text-amber-600' : 
            'text-green-600'
          }`}>
            {recommendedDifficulty.charAt(0).toUpperCase() + recommendedDifficulty.slice(1)}
          </span>
          {recommendedDifficulty === 'hard' ? (
            <ArrowUp className="w-4 h-4 text-red-600" />
          ) : recommendedDifficulty === 'easy' ? (
            <ArrowDown className="w-4 h-4 text-green-600" />
          ) : (
            <Zap className="w-4 h-4 text-amber-600" />
          )}
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-lg border shadow-sm">
        <div className="h-[350px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={difficultyData}
              margin={{ top: 10, right: 30, left: 0, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                tick={{ fill: '#6B7280', fontSize: 12 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  const formattedName = name === 'easy' ? 'Easy' : 
                                        name === 'medium' ? 'Medium' : 
                                        name === 'hard' ? 'Hard' : 
                                        'Adaptive Score';
                  return [`${value.toFixed(1)}%`, formattedName];
                }}
                labelFormatter={(label) => formatDate(label)}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB'
                }}
              />
              <Legend 
                verticalAlign="bottom" 
                height={36} 
                formatter={(value) => {
                  const formattedValue = value === 'easy' ? 'Easy' : 
                                        value === 'medium' ? 'Medium' : 
                                        value === 'hard' ? 'Hard' : 
                                        'Adaptive Score';
                  return <span className="text-sm font-medium">{formattedValue}</span>;
                }}
              />
              <ReferenceLine 
                y={85} 
                label={{ 
                  value: 'Mastery', 
                  position: 'insideTopRight',
                  fill: '#10B981',
                  fontSize: 12
                }} 
                stroke="#10B981" 
                strokeDasharray="3 3" 
              />
              <ReferenceLine 
                y={70} 
                label={{ 
                  value: 'Proficiency', 
                  position: 'insideTopRight',
                  fill: '#6366F1',
                  fontSize: 12
                }} 
                stroke="#6366F1" 
                strokeDasharray="3 3" 
              />
              <ReferenceLine 
                y={50} 
                label={{ 
                  value: 'Developing', 
                  position: 'insideTopRight',
                  fill: '#F59E0B',
                  fontSize: 12
                }} 
                stroke="#F59E0B" 
                strokeDasharray="3 3" 
              />
              <Area 
                type="monotone" 
                dataKey="easy" 
                stackId="1"
                stroke="#10B981" 
                fill="#10B981"
                fillOpacity={0.4}
              />
              <Area 
                type="monotone" 
                dataKey="medium" 
                stackId="2"
                stroke="#6366F1" 
                fill="#6366F1"
                fillOpacity={0.4}
              />
              <Area 
                type="monotone" 
                dataKey="hard" 
                stackId="3"
                stroke="#EF4444" 
                fill="#EF4444"
                fillOpacity={0.4}
              />
              <Area 
                type="monotone" 
                dataKey="adaptive" 
                stroke="#8B5CF6" 
                fill="#8B5CF6"
                fillOpacity={0.6}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {['easy', 'medium', 'hard'].map((difficulty) => {
            const recentScores = difficultyData
              .slice(-3)
              .filter((d: any) => d[difficulty] > 0)
              .map((d: any) => d[difficulty]);
              
            const averageScore = recentScores.length > 0
              ? recentScores.reduce((a: number, b: number) => a + b, 0) / recentScores.length
              : 0;
              
            return (
              <div key={difficulty} className="p-3 rounded-lg border bg-white shadow-sm">
                <h3 className={`text-sm font-medium ${
                  difficulty === 'easy' ? 'text-green-600' : 
                  difficulty === 'medium' ? 'text-blue-600' : 
                  'text-red-600'
                }`}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} Level
                </h3>
                <div className="mt-1.5 flex items-baseline">
                  <span className="text-2xl font-bold text-gray-800">
                    {averageScore.toFixed(1)}%
                  </span>
                  <span className="ml-1 text-xs text-gray-500">recent avg</span>
                </div>
                <p className="mt-1 text-xs">
                  {getDifficultyLevel(averageScore)}
                </p>
              </div>
            );
          })}
          
          <div className="p-3 rounded-lg border bg-gradient-to-br from-purple-50 to-white shadow-sm">
            <h3 className="text-sm font-medium text-purple-800">
              Adaptive Score
            </h3>
            <div className="mt-1.5 flex items-baseline">
              <span className="text-2xl font-bold text-gray-800">
                {difficultyData.length > 0 
                  ? difficultyData[difficultyData.length - 1].adaptive.toFixed(1) 
                  : '0.0'}%
              </span>
              <span className="ml-1 text-xs text-gray-500">weighted</span>
            </div>
            <p className="mt-1 text-xs">
              Combined performance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
